import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  find,
  map,
  mergeMap,
  pluck,
  share,
  timeout,
} from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OongaleeIntercom } from '../oongalee-intercom';
import { StoredToken, User, UserLogin } from '../../interfaces/user';
import { Chain } from '../../interfaces/chain';
import { IRestaurantInfo } from '../../interfaces/restaurant';
import { UserRoles } from '../../shared/enums/userRoles';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public user$ = new BehaviorSubject<User | null>(null);

  public chains = new BehaviorSubject<Chain[]>([]);
  public restaurants = new BehaviorSubject<IRestaurantInfo[]>([]);
  public areas$: Observable<'auth' | 'chains' | 'chain' | 'restaurant'>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private oongaleeIntercom: OongaleeIntercom,
  ) {
    this.areas$ = combineLatest([this.chains, this.restaurants]).pipe(
      map(([chains, restaurants]) => {
        if (chains.length > 1) {
          return 'chains';
        }
        if (chains.length === 1) {
          return 'chain';
        }
        if (restaurants.length > 0) {
          return 'restaurant';
        }
        return 'auth';
      }),
      share()
    );
  }

  public login(email: string, password: string): Observable<UserLogin> {
    const subject$ = this.http.post<UserLogin>('/auth/login', {email, password});

    subject$.pipe(untilDestroyed(this)).subscribe((data: UserLogin) => {
      this.saveTokens(data.accessToken, data.refreshToken);

      this.user$.next(data.user);

      if (this.oongaleeIntercom.connected) {
        this.oongaleeIntercom.call('push_notification.get_token');
        this.oongaleeIntercom.messages$.pipe(
          find((message) => message.event === 'push_notification.token'),
          pluck('data'),
          timeout(10000)
        ).subscribe((token) => {
          this.http.post('/users/push-notifications/subscribe', {
            token
          }).subscribe();
        });
      }

      this.router.navigate(['chains']);
    });

    return subject$;
  }

  logout(): Observable<null | boolean> {
    const unsubscribePushNotificationToken$ = this.oongaleeIntercom.messages$.pipe(
      find((message) => message.event === 'push_notification.token'),
      pluck('data'),
      timeout(10000),
      mergeMap((token) => this.http.post('/users/push-notifications/unsubscribe', {
        token
      }).pipe(
        map(() => true)
      ))
    );

    return of(this.oongaleeIntercom.connected).pipe(
      mergeMap((connected) => {

        if (connected) {
          this.oongaleeIntercom.call('push_notification.get_token');
          return unsubscribePushNotificationToken$;
        }

        return of(true);
      }),
      share(),
      catchError(() => of(false)),
      finalize(() => {
        this.userLogout();
        this.clearTokens();
        this.router.navigate(['/login']);
        this.user$.next(null);
        this.chains.next([]);
        this.restaurants.next([]);
      })
    );
  }

  private userLogout(): void {
    this.http.post<null>('/auth/logout', {}).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Logout failed', error);
        return throwError(() => error);
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  public fetchCurrentUser(): Observable<User> {
    return this.http.get<User>('/users/me');
  }

  isLoggedin(): boolean {
    return this.hasTokens();
  }

  public isSuperAdmin(): boolean {
    return this.isLoggedin() && this.user$.getValue()?.globalRole === UserRoles.superAdmin;
  }

  public isAdmin(structure: Chain | IRestaurantInfo): boolean {
    return this.isLoggedin() && [
      UserRoles.admin,
      UserRoles.restaurantAdmin,
      UserRoles.superAdmin,
    ].includes(structure.meta.userRole);
  }

  public isViewer(structure: Chain | IRestaurantInfo): boolean {
    return this.isLoggedin() && structure.meta.userRole === UserRoles.viewer;
  }

  public isWaiter(restaurant: IRestaurantInfo): boolean {
    return this.isLoggedin() && restaurant.meta.userRole === UserRoles.waiter;
  }

  public saveTokens(token: string | null, refreshToken: string | null, savedAt: number = Date.now()): void {
    if (token && refreshToken) {
      const obj: StoredToken = { token, refreshToken, savedAt };
      localStorage.setItem('tokens', JSON.stringify(obj));
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } else {
      this.clearTokens();
    }
  }

  public getTokens(): StoredToken | null {
    const tokens = this.parseStoredTokens(localStorage.getItem('tokens'));

    if (tokens) {
      return tokens;
    }

    const legacyTokens = this.parseLegacyTokens(
      localStorage.getItem('token'),
      localStorage.getItem('refreshToken')
    );

    if (legacyTokens) {
      this.saveTokens(legacyTokens.token, legacyTokens.refreshToken, legacyTokens.savedAt);
    }

    return legacyTokens;
  }

  public get tokenSavedAt(): number | null {
    return this.getTokens()?.savedAt ?? null;
  }

  public clearTokens(): void {
    localStorage.removeItem('tokens');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }

  public hasTokens(): boolean {
    return this.getTokens() !== null;
  }

  get token(): string | null {
    return this.getTokens()?.token ?? null;
  }

  private parseStoredTokens(value: string | null): StoredToken | null {
    if (!value) {
      return null;
    }

    try {
      const parsed = JSON.parse(value);

      if (parsed?.token && parsed?.refreshToken && parsed?.savedAt) {
        return parsed;
      }
    } catch (error) {
      console.error('Failed to parse stored tokens', error);
    }

    return null;
  }

  private parseLegacyTokens(token: string | null, refreshToken: string | null): StoredToken | null {
    if (!token || !refreshToken) {
      return null;
    }

    try {
      const parsed = JSON.parse(token);

      if (parsed?.value) {
        return {
          token: parsed.value,
          refreshToken,
          savedAt: parsed.savedAt ?? Date.now(),
        };
      }
    } catch (error) {
      return {
        token,
        refreshToken,
        savedAt: Date.now(),
      };
    }

    return null;
  }

  public area(): 'auth' | 'chains' | 'chain' | 'restaurant' {
    if (this.isSuperAdmin()) {
      return 'chains';
    }

    if (this.chains.getValue().length > 1) {
      return 'chains';
    }

    if (this.chains.getValue().length === 1) {
      return 'chain';
    }

    if (this.restaurants.getValue().length > 0) {
      return 'restaurant';
    }

    return 'auth';
  }
}
